import { MEDICINE_REPORT_MAX_FARMS, type MedicineCountDatesFarm, type MedicineReportColumn, type MedicineReportRequestFarm } from '@/types';

/** The farms a report starts with ticked, and what Select all ticks: the first of the offered farms, as many as one report compares. */
export function defaultReportFarmIds(farms: { farm_id: number }[]): number[] {
    return farms.slice(0, MEDICINE_REPORT_MAX_FARMS).map((farm) => farm.farm_id);
}

/** The count days picked for each farm, by farm id, earlier first whatever order they were ticked in; a farm with fewer than two picked is not ready. */
export type ReportDateSelection = Record<number, string[]>;

/** What a farm's count days allow: two or more to compare, a single one with nothing to compare it to, or none. */
export type CountDatesAvailability = 'comparable' | 'single' | 'none';

export function availabilityOf(farm: Pick<MedicineCountDatesFarm, 'dates'>): CountDatesAvailability {
    if (farm.dates.length >= 2) {
        return 'comparable';
    }

    return farm.dates.length === 1 ? 'single' : 'none';
}

/**
 * Ticks or unticks a day, keeping the days earlier first (`yyyy-MM-dd` sorts by date) so the first is
 * always the start; a third day cannot be ticked while two are, so exactly two can ever be compared.
 */
export function toggleCountDate(selected: string[], date: string): string[] {
    if (selected.includes(date)) {
        return selected.filter((picked) => picked !== date);
    }

    return selected.length < 2 ? [...selected, date].sort() : selected;
}

/** Whether a farm has both its days picked. */
export function hasBothDates(selection: ReportDateSelection, farmId: number): boolean {
    return (selection[farmId] ?? []).length === 2;
}

/** A farm's count days newest first, grouped by month (`yyyy-MM`), so a long history scans by month. */
export function countDatesByMonth(dates: string[]): { month: string; dates: string[] }[] {
    const months: { month: string; dates: string[] }[] = [];

    [...dates]
        .sort()
        .reverse()
        .forEach((date) => {
            const month = date.slice(0, 7);
            const last = months[months.length - 1];

            if (last?.month === month) {
                last.dates.push(date);
            } else {
                months.push({ month, dates: [date] });
            }
        });

    return months;
}

/** Every farm that can be compared has exactly two days picked, and at least one farm can be compared. */
export function isDateSelectionComplete(farms: MedicineCountDatesFarm[], selection: ReportDateSelection): boolean {
    const comparable = farms.filter((farm) => availabilityOf(farm) === 'comparable');

    return comparable.length > 0 && comparable.every((farm) => hasBothDates(selection, farm.farm_id));
}

/** The report request: each comparable farm with its two days, earlier first. */
export function reportRequestOf(farms: MedicineCountDatesFarm[], selection: ReportDateSelection): MedicineReportRequestFarm[] {
    return farms
        .filter((farm) => availabilityOf(farm) === 'comparable')
        .map((farm) => {
            const [start, end] = selection[farm.farm_id] ?? [];

            return { farm_id: farm.farm_id, start_date: start, end_date: end };
        });
}

export type ReportMetricId = 'start' | 'bought' | 'used' | 'wasted' | 'expected' | 'end' | 'deviation';

/** The rows of a farm's report table, top to bottom. */
export const REPORT_METRIC_IDS: readonly ReportMetricId[] = ['start', 'bought', 'used', 'wasted', 'expected', 'end', 'deviation'];

/** The quantity metric of a column; the deviation is a percentage and read through `deviation_percent` instead. */
export function quantityMetricOf(column: MedicineReportColumn, metric: Exclude<ReportMetricId, 'deviation'>): number | null {
    return column[metric];
}
